import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { setLoading, setSnackInfo } from '../redux/actions/appAction';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import { Box, Typography, IconButton, CircularProgress } from '@material-ui/core';
import HttpService from '../services/httpService';
import { commaSeparateAmount } from './commaSeparate';
import { NarenEmail, salesVsQuotationVariancePercent } from '../utils';
import {
    AuditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InboxOutlined,
    CloseOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const ORANGE = '#F47E44';
const ORANGE_DARK = '#c1531f';
const ORANGE_LIGHT = '#fff1ea';
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const GREEN = '#1e8449';
const GREEN_GRADIENT = `linear-gradient(135deg, #146c43, ${GREEN})`;
const RED = '#c0392b';
const RED_GRADIENT = `linear-gradient(135deg, #922b21, ${RED})`;
const HEADING_FONT = "'Silka bold', 'Silka semiBold', sans-serif !important";

const useStyles = makeStyles(() => ({
    page: { padding: 20, background: '#f6f7fb', minHeight: '100vh' },
    headerCard: {
        marginBottom: 20,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 10px 28px rgba(196,83,31,0.2)',
        background: HEADER_GRADIENT,
        padding: '22px 26px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
    headerRight: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    headerIconWrap: {
        width: 52,
        height: 52,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.22)',
        border: '2px solid rgba(255,255,255,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    statChip: {
        background: 'rgba(255,255,255,0.2)',
        color: '#fff',
        borderRadius: 20,
        padding: '7px 16px',
        fontWeight: 700,
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
    },
    refreshBtn: {
        borderRadius: 999,
        border: 'none',
        background: '#fff',
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 12.5,
        padding: '8px 16px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.12), 0 3px 10px rgba(0,0,0,0.18)',
        '&:disabled': { opacity: 0.75, cursor: 'wait' },
    },
    infoCard: {
        borderRadius: 16,
        background: '#fff',
        boxShadow: '0 6px 20px rgba(17,24,39,0.06)',
        padding: '16px 20px',
        marginBottom: 20,
        fontSize: 13.5,
        color: '#5c6570',
        lineHeight: 1.5,
    },
    tableCard: {
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 6px 20px rgba(17,24,39,0.06)',
        background: '#fff',
    },
    tableWrap: { maxHeight: 680, overflow: 'auto' },
    tableHeadCell: {
        backgroundColor: ORANGE_DARK,
        color: '#fff',
        fontWeight: 700,
        fontSize: 13,
        textAlign: 'center',
        whiteSpace: 'nowrap',
    },
    tableRow: {
        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
        '&:hover': { backgroundColor: ORANGE_LIGHT },
        transition: 'background-color .12s ease',
    },
    tableCellCss: { textAlign: 'center', fontSize: 13.5 },
    varianceBadge: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 20,
        background: '#fdecea',
        color: RED,
        fontWeight: 700,
        fontSize: 12,
    },
    leadLink: {
        color: ORANGE_DARK,
        fontWeight: 700,
        textDecoration: 'underline',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
    },
    actionBtnBase: {
        borderRadius: 10,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        padding: '6px 14px',
        height: 'auto',
    },
    approveBtn: {
        color: '#fff',
        background: GREEN_GRADIENT,
        '&:hover': {
            background: 'linear-gradient(135deg, #0f5132, #146c43)',
            boxShadow: '0 6px 16px rgba(30,132,73,0.3)',
        },
    },
    rejectBtn: {
        color: RED,
        border: `1.5px solid ${RED}`,
        background: '#fff',
        '&:hover': { background: '#fdecea' },
    },
    emptyState: { padding: '60px 20px', textAlign: 'center', color: '#9aa1ac' },
    emptyIconWrap: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: ORANGE_LIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 14px',
    },
    dialogHeader: {
        color: '#fff',
        padding: '18px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
    },
    dialogHeaderTitle: { display: 'flex', alignItems: 'center', gap: 10 },
    dialogCloseBtn: {
        color: 'rgba(255,255,255,0.9)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
    },
    clearBtn: {
        color: '#6b7280',
        border: '1.5px solid #d8dee8',
        background: '#fff',
        borderRadius: 10,
        textTransform: 'none',
        fontWeight: 600,
    },
    submitApproveBtn: {
        color: '#fff',
        background: GREEN_GRADIENT,
        borderRadius: 10,
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': { background: 'linear-gradient(135deg, #0f5132, #146c43)' },
    },
    submitRejectBtn: {
        color: '#fff',
        background: RED_GRADIENT,
        borderRadius: 10,
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': { background: 'linear-gradient(135deg, #7b241c, #922b21)' },
    },
    accessCard: {
        maxWidth: 520,
        margin: '48px auto',
        padding: 32,
        borderRadius: 16,
        background: '#fff',
        boxShadow: '0 6px 20px rgba(17,24,39,0.08)',
        textAlign: 'center',
    },
}));

function DsoPaymentApprovalPage(props) {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const [approvalData, setApprovalData] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [remark, setRemark] = useState('');
    const [approvalType, setApprovalType] = useState('');
    const [rowData, setRowData] = useState(null);

    const canAccessPage = userDetails?.email === NarenEmail.email;

    useEffect(() => {
        if (canAccessPage) {
            getPendingApprovals();
        }
    }, [canAccessPage]);

    const getPendingApprovals = async () => {
        setListLoading(true);
        try {
            const res = await HttpService.getDsoPaymentApprovalData();
            if (res.status === 200) {
                setApprovalData(res.data || []);
            }
        } catch (error) {
            console.error(error);
            props.setSnackInfo('Something went wrong', 'error');
        }
        setListLoading(false);
    };

    const handleApprove = (row) => {
        setOpenDialog(true);
        setApprovalType('Approved');
        setRowData(row);
        setRemark('');
    };

    const handleReject = (row) => {
        setOpenDialog(true);
        setApprovalType('Rejected');
        setRowData(row);
        setRemark('');
    };

    const submitApproval = async () => {
        try {
            setSubmitting(true);
            const obj = {
                remark,
                approvalType,
                leadId: rowData?._id,
            };
            const res = await HttpService.approveRejectDsoPaymentApproval(obj);
            if (res.status === 200) {
                props.setSnackInfo(`${res.data.type} successfully`, 'success');
                setOpenDialog(false);
                getPendingApprovals();
            }
        } catch (err) {
            console.error(err);
            props.setSnackInfo(err?.response?.data?.message || 'Something went wrong', 'error');
        }
        setSubmitting(false);
    };

    if (!canAccessPage) {
        return (
            <div className={classes.page}>
                <Box className={classes.accessCard}>
                    <Typography style={{ fontFamily: HEADING_FONT, fontSize: 20, marginBottom: 12 }}>
                        DSO Approval
                    </Typography>
                    <Typography style={{ color: '#6b7280', fontSize: 14 }}>
                        You do not have access to this page. Only the DGM approver ({NarenEmail.email}) can
                        approve or reject Internal Design Sign-Off requests when the design amount is 30% or
                        more below sales won.
                    </Typography>
                </Box>
            </div>
        );
    }

    const pendingCount = approvalData.length;

    return (
        <div className={classes.page}>
            <Box className={classes.headerCard}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.headerIconWrap}>
                        <AuditOutlined style={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography style={{ color: '#fff', fontFamily: HEADING_FONT, fontSize: 21, margin: 0 }}>
                            DSO Approval
                        </Typography>
                        <Typography style={{ color: 'rgba(255,255,255,0.88)', fontSize: 13.5, marginTop: 4 }}>
                            Internal Design Sign-Off when design value is ≥30% below sales won
                        </Typography>
                    </Box>
                </Box>
                <Box className={classes.headerRight}>
                    <Box className={classes.statChip}>
                        <AuditOutlined style={{ fontSize: 14 }} />
                        {pendingCount} Pending request{pendingCount === 1 ? '' : 's'}
                    </Box>
                    <button
                        type="button"
                        className={classes.refreshBtn}
                        onClick={getPendingApprovals}
                        disabled={listLoading}
                    >
                        <ReloadOutlined style={{ fontSize: 14 }} />
                        Refresh
                    </button>
                </Box>
            </Box>

            <Box className={classes.infoCard}>
                Approve to allow the designer to complete Internal Design Sign-Off for the selected quotation
                amount. Reject returns the lead to the designer without approval.
            </Box>

            <Box className={classes.tableCard}>
                <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader aria-label="dso payment approval">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeadCell}>Sl No.</TableCell>
                                <TableCell className={classes.tableHeadCell}>Project No.</TableCell>
                                <TableCell className={classes.tableHeadCell}>Customer Name</TableCell>
                                <TableCell className={classes.tableHeadCell}>Sales Won Amount</TableCell>
                                <TableCell className={classes.tableHeadCell}>Design Sign Off Amount</TableCell>
                                <TableCell className={classes.tableHeadCell}>Reduction %</TableCell>
                                <TableCell className={classes.tableHeadCell}>Lead No.</TableCell>
                                <TableCell className={classes.tableHeadCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {listLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} style={{ border: 'none', textAlign: 'center', padding: '50px 0' }}>
                                        <CircularProgress size={30} style={{ color: ORANGE }} />
                                        <Typography style={{ color: '#9aa1ac', marginTop: 10 }}>
                                            Loading requests...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : approvalData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} style={{ border: 'none' }}>
                                        <Box className={classes.emptyState}>
                                            <Box className={classes.emptyIconWrap}>
                                                <InboxOutlined style={{ fontSize: 34, color: ORANGE_DARK }} />
                                            </Box>
                                            <Typography style={{ fontWeight: 600, color: '#7d8794' }}>
                                                No pending DSO approvals
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#a4abb5', marginTop: 4 }}>
                                                New requests appear when a designer sends Internal Design Sign-Off
                                                for DGM approval.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                approvalData.map((row, i) => {
                                    const reduction = salesVsQuotationVariancePercent(
                                        row?.staticSalesWonAmount,
                                        row?.dsoPaymentPendingSignOffAmount
                                    );
                                    return (
                                        <TableRow key={row._id} className={classes.tableRow}>
                                            <TableCell className={classes.tableCellCss}>{i + 1}</TableCell>
                                            <TableCell className={classes.tableCellCss}>
                                                {row?.erpProjectNo || '—'}
                                            </TableCell>
                                            <TableCell className={classes.tableCellCss}>
                                                {row?.customerId?.name || '—'}
                                            </TableCell>
                                            <TableCell className={classes.tableCellCss}>
                                                ₹ {commaSeparateAmount(row?.staticSalesWonAmount) || 'NA'}
                                            </TableCell>
                                            <TableCell className={classes.tableCellCss}>
                                                ₹ {commaSeparateAmount(row?.dsoPaymentPendingSignOffAmount) || 'NA'}
                                            </TableCell>
                                            <TableCell className={classes.tableCellCss}>
                                                <span className={classes.varianceBadge}>
                                                    {reduction.toFixed(1)}%
                                                </span>
                                            </TableCell>
                                            <TableCell className={classes.tableCellCss}>
                                                <Link
                                                    to={`/project-logs/${row?._id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={classes.leadLink}
                                                >
                                                    {row?.lead_no || 'N/A'}
                                                    <OpenInNewIcon style={{ fontSize: 14 }} />
                                                </Link>
                                            </TableCell>
                                            <TableCell className={classes.tableCellCss}>
                                                <Box display="flex" justifyContent="center" style={{ gap: 8 }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleApprove(row)}
                                                        className={`${classes.actionBtnBase} ${classes.approveBtn}`}
                                                        startIcon={<CheckCircleOutlined />}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleReject(row)}
                                                        className={`${classes.actionBtnBase} ${classes.rejectBtn}`}
                                                        startIcon={<CloseCircleOutlined />}
                                                    >
                                                        Reject
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Dialog
                open={openDialog}
                onClose={() => !submitting && setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
            >
                <Box
                    className={classes.dialogHeader}
                    style={{
                        background: approvalType === 'Rejected' ? RED_GRADIENT : GREEN_GRADIENT,
                    }}
                >
                    <Box className={classes.dialogHeaderTitle}>
                        {approvalType === 'Rejected' ? (
                            <CloseCircleOutlined style={{ fontSize: 20 }} />
                        ) : (
                            <CheckCircleOutlined style={{ fontSize: 20 }} />
                        )}
                        <Typography style={{ fontFamily: HEADING_FONT, fontSize: 17 }}>
                            {approvalType === 'Rejected' ? 'Reject DSO request' : 'Approve DSO request'}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        className={classes.dialogCloseBtn}
                        onClick={() => setOpenDialog(false)}
                        disabled={submitting}
                    >
                        <CloseOutlined style={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
                <DialogContent style={{ paddingTop: 20 }}>
                    {rowData && (
                        <Typography variant="body2" style={{ color: '#6b7280', marginBottom: 16 }}>
                            {rowData.erpProjectNo} · {rowData.customerId?.name}
                        </Typography>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Remarks (optional)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        multiline
                        minRows={2}
                    />
                </DialogContent>
                <DialogActions style={{ padding: '14px 24px 20px' }}>
                    <Button
                        onClick={() => setOpenDialog(false)}
                        variant="outlined"
                        className={classes.clearBtn}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submitApproval}
                        variant="contained"
                        disabled={submitting}
                        className={
                            approvalType === 'Rejected' ? classes.submitRejectBtn : classes.submitApproveBtn
                        }
                    >
                        {submitting ? 'Submitting…' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default connect(null, { setLoading, setSnackInfo })(DsoPaymentApprovalPage);
